import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/config";
import { Star, Quote, Loader2, PenLine } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-1" data-testid="review-rating-picker">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="p-1"
        data-testid={`rating-star-${n}`}
        aria-label={`${n} star${n > 1 ? "s" : ""}`}
      >
        <Star className={`h-7 w-7 transition-colors ${n <= value ? "fill-boco-green text-boco-green" : "text-slate-300"}`} />
      </button>
    ))}
  </div>
);

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", rating: 5, text: "" });

  const load = () => api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) { toast.error("Please add your name and review"); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thank you! Your review was submitted and will appear once approved.");
      setForm({ name: "", location: "", rating: 5, text: "" });
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="bg-white border-y border-slate-100 text-slate-900">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-boco-green">
            {[...Array(5)].map((_, i) => (<Star key={`h-star-${i}`} className="h-5 w-5 fill-boco-green" />))}
          </div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black text-boco-navy tracking-tight">
            Loved by Our Neighbors
          </h2>
          <p className="mt-3 text-slate-600">What local families across Botetourt County are saying.</p>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="mt-6 inline-flex items-center gap-2 rounded-full bg-boco-green px-6 py-3 font-bold text-boco-navy hover:bg-boco-greendark hover:text-white transition-colors" data-testid="leave-review-btn">
                <PenLine className="h-4 w-4" /> Leave a Review
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white text-slate-900">
              <DialogHeader>
                <DialogTitle className="font-display text-boco-navy">Share your experience</DialogTitle>
                <DialogDescription>Tell us how BOCO BINS did — your review appears on the site once approved.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-boco-navy font-semibold">Your Rating</Label>
                  <div className="mt-1.5"><StarPicker value={form.rating} onChange={(r) => setForm({ ...form, rating: r })} /></div>
                </div>
                <div>
                  <Label className="text-boco-navy font-semibold">Name</Label>
                  <Input className="mt-1.5 text-slate-900" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name" />
                </div>
                <div>
                  <Label className="text-boco-navy font-semibold">Town (optional)</Label>
                  <Input className="mt-1.5 text-slate-900" placeholder="e.g. Buchanan, VA" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="review-location" />
                </div>
                <div>
                  <Label className="text-boco-navy font-semibold">Your Review</Label>
                  <Textarea className="mt-1.5 text-slate-900" rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="review-text" />
                </div>
                <DialogFooter>
                  <button type="submit" disabled={submitting} className="w-full rounded-full bg-boco-navy py-3 font-bold text-white hover:bg-boco-navylight transition-colors disabled:opacity-70 flex items-center justify-center gap-2" data-testid="review-submit">
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit Review"}
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {reviews.length > 0 && (
          <div className="mt-12 flex flex-wrap justify-center gap-6">
            {reviews.map((r, i) => (
              <motion.div
                key={r.id || r.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="relative rounded-3xl bg-[#F8FAFC] border border-slate-200 p-6 flex flex-col w-full sm:w-80"
                data-testid={`review-card-${i}`}
              >
                <Quote className="h-8 w-8 text-boco-green/30" />
                <div className="mt-2 flex gap-0.5">
                  {[...Array(r.rating)].map((_, s) => (<Star key={`${r.id}-star-${s}`} className="h-4 w-4 fill-boco-green text-boco-green" />))}
                </div>
                <p className="mt-3 text-slate-700 text-sm leading-relaxed flex-1">"{r.text}"</p>
                <div className="mt-5 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-boco-navy text-white font-display font-bold">
                    {r.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold text-boco-navy text-sm">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.location}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
